<template>
    <article>
        <p>
            I come from the database world where observability, or monitoring as we used to call it, was always really
            important to keep databases up and running and operating well.
            Thousands of data points would be collected and displayed in countless graphs.
            As an expert DBA, you can see every detail about internal database operations and feel very
            good about yourself being able to put all this data together and resolve the puzzle.
        </p>

        <div class="my-6">
            <v-img class="mx-auto"  max-width="800px" eager src="/static/img/blog/simplify-observability/old_style_dashboard.png" />
        </div>

        <p>
            Yet the world has changed. First, many modern applications are designed based on microservices architecture,
            or otherwise have many different components communicating with each other through a network.
            In this case, figuring out which component is the cause of the issue is the first task - before fixing the problem, you need to know where it is.
        </p>

        <p>
            Secondly, we have increased developer ownership of the overall environment - think about DevOps or SRE disciplines.
            Application developers tend to focus on their applications working properly and have less expertise in the intricate details of infrastructure operations or time and desire to learn about them.
            This means detailed dashboards showing every detail of internal operations are useless for them.
        </p>

        <p>
            But it gets worse. Because of the complexity of deployment and configuration in many modern observability systems,
            a lot of components end up being completely uninstrumented.
            So, while you have an overload of information, you also tend to have huge gaps, meaning some issues will be invisible and undiagnosable.
        </p>

        <p>
            So what do we need instead? We need simplified Observability.
            Instead of even more data—logs, metrics, traces, events, you name it—we need actionable insights to find the root cause of the issue in a way that can be implemented by experts, or better yet, by computers.
            This is what real AIOps should be.
        </p>

        <p>
            Even better, we need to prevent problems before they happen by recognizing unsustainable and dangerous patterns.
        </p>

        <h2>Meet Coroot</h2>

        <p>
            This is the approach we're taking with Coroot, focusing on providing you a powerful observability platform with a
            focus on simplicity—helping you resolve issues quickly, rather than overloading you with information.
        </p>

        <v-card class="pa-6 my-6">
            <v-img class="mx-auto" eager src="/static/img/blog/simplify-observability/health.png" />
        </v-card>

        <p>
            First, Coroot looks at your complete environment, covering every component, service, and application, so you do not have information gaps.
            Coroot supports components deployed on Kubernetes, traditional VMs, bare-metal deployments, as well as cloud services such as DBaaS.
        </p>

        <p>
            Second, Coroot is very simple to deploy. We leverage modern, powerful instrumentation features provided by the Linux kernel,
            such as eBPF, allowing us to gather the most important information with zero configuration.
            Coroot also identifies components that can provide additional information and assists in configuring them.
        </p>

        <p>
            Third, Coroot focuses on providing you with the most important information and actionable insights,
            enabling you to identify and resolve 80% of issues much faster than with legacy observability solutions.
        </p>

        <p>
            Interested in learning more? Check out this <a href="/overview" target="_blank">detailed overview of Coroot features</a>.
        </p>

        <h2>Open Source</h2>

        <p>
            All these features are available in the Coroot Open Source edition, which you can self-host.
            We also plan to release an Enterprise Edition available through subscription, containing additional features
            required for large enterprises and complex environments.
        </p>

        <h2>Coroot Cloud</h2>

        <p>
            If you prefer a hosted solution, there is <a href="/cloud" target="_blank">Coroot Cloud</a>, which comes with a free trial and can be a great way to explore Coroot.
            You will also be pleasantly surprised by our simple, transparent <a href="/pricing" target="_blank">pricing</a>, offering substantial cost savings compared to legacy alternatives.
        </p>

        <h2>Future</h2>

        <p>
            While Coroot is already an awesome open-source observability platform with a focus on simplicity, we have ambitious plans to take it even further.
            Our goal is to help you prevent as many issues as possible, and if they do occur, have them resolved automatically or with minimal human
            interaction required.
        </p>

        <p>
            If you have ideas, please let us know by creating an issue or a pull request on <a href="https://github.com/coroot/coroot" target="_blank">Github repository</a>.
            Also, consider subscribing to the Coroot Newsletter to stay updated on our exciting product developments.
        </p>

        <p>Reach out to us on <a href="https://join.slack.com/t/coroot-community/shared_invite/zt-1gsnfo0wj-I~Zvtx5CAAb8vr~r~vecyw" target="_blank"><v-icon>mdi-slack</v-icon>Slack</a>,
            <a href="https://twitter.com/coroot_com" target="_blank"><v-icon>mdi-twitter</v-icon>Twitter</a>, or <a href="https://www.linkedin.com/company/coroot" target="_blank"><v-icon>mdi-linkedin</v-icon>LinkedIn</a>.</p>

        <p>
            If you like Coroot, give us a ⭐ on <a href="https://github.com/coroot/coroot" target="_blank">GitHub</a>️ –
            it helps us reach more engineers and improve our product for you.
        </p>

    </article>
</template>

<script>


export default {}
</script>

<style scoped>
</style>
